/**
 * Lightweight storefront conversion helpers.
 * Keeps the exit reminder and configured sale timers without fabricating
 * inventory, purchases, or email/cart-recovery behavior.
 */
window.AnimeConversion = (function() {
  'use strict';

  const state = {
    initialized: false,
    exitIntentShown: false,
    sessionStartTime: Date.now(),
    previouslyFocused: null
  };

  function escapeHtml(value) {
    const element = document.createElement('span');
    element.textContent = String(value || '');
    return element.innerHTML;
  }

  function getStorefrontRoute(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/$/, '')}/${String(path).replace(/^\//, '')}`;
  }

  function getCart() {
    if (window.AnimeCart?.getCart) {
      return window.AnimeCart.getCart();
    }

    return { items: [], item_count: 0, total_price: 0 };
  }

  function readSessionValue(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeSessionValue(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage is optional and can be unavailable in privacy modes.
    }
  }

  function removeSessionValue(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Ignore unavailable storage.
    }
  }

  function shouldShowExitIntent() {
    if (state.exitIntentShown || document.querySelector('.exit-intent-popup')) return false;
    if (window.location.pathname.includes('/checkout')) return false;
    if (window.AnimeCart?.isDrawerOpen?.()) return false;

    const anotherModalIsOpen = [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]')]
      .some((dialog) => !dialog.closest('[aria-hidden="true"]') && dialog.offsetParent !== null);
    if (anotherModalIsOpen) return false;

    const cart = getCart();
    if (!cart || cart.item_count === 0) return false;

    const configuredDelay = Number(window.themeSettings?.exitIntentDelay);
    const minimumDelay = Number.isFinite(configuredDelay) ? configuredDelay : 30000;
    return Date.now() - state.sessionStartTime >= minimumDelay;
  }

  function getCartMessage(cart) {
    const names = [...new Set((cart.items || []).map((item) => item.product_title || item.title).filter(Boolean))];
    if (names.length === 0) return 'Your items are ready when you are.';

    if (names.length === 1) {
      return `<strong>${escapeHtml(names[0])}</strong> is ready when you are.`;
    }

    const escapedNames = names.map(escapeHtml);
    const lastName = escapedNames.pop();
    return `<strong>${escapedNames.join('</strong>, <strong>')}</strong> and <strong>${lastName}</strong> are ready when you are.`;
  }

  function trackEvent(eventName, data) {
    try {
      window.Shopify?.analytics?.publish?.(eventName, data);
    } catch (error) {
      // Analytics must never block the storefront flow.
    }
  }

  function showExitIntentPopup() {
    if (!shouldShowExitIntent()) return;

    state.exitIntentShown = true;
    state.previouslyFocused = document.activeElement;

    const cart = getCart();
    const shippingThreshold = Number(window.themeSettings?.freeShippingItemThreshold) || 1;
    const shippingMessage = shippingThreshold <= 1
      ? 'Free shipping on every order'
      : `Free shipping on ${shippingThreshold}+ towels`;
    const popup = document.createElement('div');
    popup.className = 'exit-intent-popup';
    popup.innerHTML = `
      <div class="exit-popup-overlay" aria-hidden="true"></div>
      <section class="exit-popup-content" role="dialog" aria-modal="true" aria-labelledby="exit-popup-title" aria-describedby="exit-popup-message">
        <button type="button" class="exit-popup-close" aria-label="Close cart reminder">×</button>
        <div class="exit-popup-header">
          <h2 class="exit-popup-title" id="exit-popup-title">Your cart is saved</h2>
        </div>
        <div class="exit-popup-body">
          <p class="cart-value-message" id="exit-popup-message">${getCartMessage(cart)}</p>
          <div class="exit-popup-actions">
            <button type="button" class="complete-order-btn anime-button">Checkout</button>
            <button type="button" class="keep-shopping-btn">Keep shopping</button>
          </div>
          <p class="value-message">${shippingMessage}</p>
        </div>
      </section>
    `;

    popup.querySelector('.exit-popup-overlay').addEventListener('click', closeExitPopup);
    popup.querySelector('.exit-popup-close').addEventListener('click', closeExitPopup);
    popup.querySelector('.keep-shopping-btn').addEventListener('click', closeExitPopup);
    popup.querySelector('.complete-order-btn').addEventListener('click', completeOrder);
    popup.addEventListener('keydown', handlePopupKeydown);

    document.body.appendChild(popup);
    document.documentElement.classList.add('cart-reminder-open');
    popup.querySelector('.exit-popup-close').focus();

    trackEvent('exit_intent_shown', { cart_value: cart.total_price, item_count: cart.item_count });
  }

  function handlePopupKeydown(event) {
    if (event.key === 'Escape') {
      closeExitPopup();
      return;
    }

    if (event.key !== 'Tab') return;
    const popup = event.currentTarget;
    const focusable = [...popup.querySelectorAll('.exit-popup-content button:not([disabled]), .exit-popup-content a[href], .exit-popup-content input:not([disabled])')];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function closeExitPopup() {
    const popup = document.querySelector('.exit-intent-popup');
    document.documentElement.classList.remove('cart-reminder-open');
    if (!popup) return;

    popup.remove();
    if (state.previouslyFocused instanceof HTMLElement) {
      state.previouslyFocused.focus({ preventScroll: true });
    }
  }

  function completeOrder() {
    closeExitPopup();
    window.location.assign(getStorefrontRoute('checkout'));
  }

  function initExitIntent() {
    document.addEventListener('mouseout', (event) => {
      if (!event.relatedTarget && event.clientY <= 0) {
        showExitIntentPopup();
      }
    });

    let previousScrollTop = window.scrollY;
    window.addEventListener('scroll', () => {
      const currentScrollTop = window.scrollY;
      if (previousScrollTop - currentScrollTop > 50 && currentScrollTop < 200) {
        showExitIntentPopup();
      }
      previousScrollTop = currentScrollTop;
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (shouldShowExitIntent()) writeSessionValue('show_exit_intent_on_return', 'true');
        return;
      }

      if (readSessionValue('show_exit_intent_on_return') === 'true') {
        removeSessionValue('show_exit_intent_on_return');
        window.setTimeout(showExitIntentPopup, 1000);
      }
    });
  }

  function initSaleTimers() {
    document.querySelectorAll('[data-sale-timer]').forEach((timer) => {
      const endTime = new Date(timer.dataset.saleTimer);
      if (Number.isNaN(endTime.getTime())) return;

      let interval;
      const update = () => {
        const remaining = endTime.getTime() - Date.now();
        if (remaining <= 0) {
          timer.textContent = 'Sale ended';
          if (interval) window.clearInterval(interval);
          return;
        }

        const hours = Math.floor(remaining / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        timer.textContent = `${hours}h ${minutes}m ${seconds}s`;
      };

      update();
      interval = window.setInterval(update, 1000);
    });
  }

  function addConversionStyles() {
    if (document.getElementById('anime-conversion-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'anime-conversion-styles';
    styles.textContent = `
      .exit-intent-popup {
        position: fixed;
        inset: 0;
        z-index: 99999;
        display: grid;
        place-items: center;
        padding: 1rem;
      }

      .cart-reminder-open,
      .cart-reminder-open body {
        overflow: hidden;
      }

      .exit-popup-overlay {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border: 0;
        background: rgba(17, 24, 39, 0.62);
        cursor: default;
      }

      .exit-popup-content {
        position: relative;
        width: min(420px, 100%);
        max-height: calc(100vh - 2rem);
        max-height: calc(100dvh - 2rem);
        overflow-y: auto;
        padding: 2rem;
        border: 2px solid var(--color-primary);
        border-radius: 20px;
        background: #ffffff;
        color: #111827;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
      }

      .exit-popup-close {
        position: absolute;
        inset-block-start: 0.875rem;
        inset-inline-end: 0.875rem;
        width: 40px;
        height: 40px;
        border: 0;
        border-radius: 50%;
        background: #f3f4f6;
        color: #111827;
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1;
        cursor: pointer;
      }

      .exit-popup-close:hover,
      .exit-popup-close:focus-visible {
        background: #e5e7eb;
      }

      .exit-popup-header,
      .exit-popup-body {
        text-align: center;
      }

      .exit-popup-title {
        margin: 0 2.5rem 0.75rem;
        color: var(--color-primary);
        font-size: 2rem;
        line-height: 1.1;
      }

      .cart-value-message {
        margin: 0 0 1.25rem;
        color: #374151;
        font-size: 1rem;
        line-height: 1.5;
      }

      .exit-popup-actions {
        display: grid;
        gap: 0.75rem;
      }

      .complete-order-btn,
      .keep-shopping-btn {
        min-height: 48px;
        width: 100%;
        border-radius: 14px;
        font-size: 0.95rem;
        font-weight: 700;
      }

      .complete-order-btn.anime-button {
        background: var(--color-primary);
        color: #ffffff !important;
        letter-spacing: 0.04em;
      }

      .complete-order-btn.anime-button:hover,
      .complete-order-btn.anime-button:focus-visible {
        background: var(--color-primary);
      }

      .keep-shopping-btn {
        border: 2px solid #111827;
        background: #ffffff;
        color: #111827;
        font: inherit;
        font-weight: 700;
        cursor: pointer;
      }

      .keep-shopping-btn:hover,
      .keep-shopping-btn:focus-visible {
        background: #111827;
        color: #ffffff;
      }

      .value-message {
        margin: 0.875rem 0 0;
        padding: 0.625rem 0.75rem;
        border-radius: 10px;
        background: #ecfdf5;
        color: #065f46;
        font-size: 0.825rem;
        font-weight: 600;
      }

      @media (max-width: 767px) {
        .exit-intent-popup {
          place-items: end center;
          padding: 0;
        }

        .exit-popup-content {
          width: 100%;
          max-height: calc(100vh - 1rem);
          max-height: calc(100dvh - 1rem);
          padding: 1.25rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom));
          border-width: 2px 0 0;
          border-radius: 22px 22px 0 0;
        }

        .exit-popup-title {
          margin: 0 2.75rem 0.625rem 0;
          text-align: left;
          font-size: 1.5rem;
        }

        .exit-popup-body {
          text-align: left;
        }

        .cart-value-message {
          margin-block-end: 1rem;
          font-size: 0.925rem;
        }

        .exit-popup-actions {
          grid-template-columns: 1fr 1fr;
        }

        .value-message {
          text-align: center;
        }
      }
    `;
    document.head.appendChild(styles);
  }

  function showToast(message, type = 'info') {
    if (window.AnimeCart?.showToast) {
      return window.AnimeCart.showToast(message, type);
    }

    const toast = document.createElement('div');
    toast.className = `anime-toast ${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('show'));
    window.setTimeout(() => toast.remove(), 3000);
  }

  function init() {
    if (state.initialized) return;
    state.initialized = true;
    addConversionStyles();
    initExitIntent();
    initSaleTimers();
  }

  return {
    init,
    closeExitPopup,
    completeOrder,
    showToast
  };
})();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.AnimeConversion.init, { once: true });
} else {
  window.AnimeConversion.init();
}
